import { Checkbox, CircularProgress } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import classNames from "classnames";
import * as React from "react";

import { ErrorBoundary } from "@/shared/components/ErrorBoundary";

import { type EnumOption } from "../FilterDefinition";
import { type EnumComparator, isValueless } from "../FilterSchema";

import styles from "./EnumValueInput.module.css";

interface EnumValueInputProps {
  comparator: EnumComparator;
  value: string[];
  onChange: (next: string[]) => void;
  options: EnumOption[] | (() => Promise<EnumOption[]>);
}

interface NormalizedOption {
  label: string;
  value: string;
}

function normalizeOption(option: EnumOption): NormalizedOption {
  return typeof option === "string" ? { label: option, value: option } : option;
}

/**
 * Value input for enum-typed filters.
 *
 * Operator-aware: CONTAINS ("is") and NOT_CONTAINS ("is not") render a
 * searchable checkbox list.
 *
 * ``options`` accepts either ``string[]`` (wire value === display
 * label) or ``LabeledEnumOption[]`` (wire ``value`` and human ``label``
 * differ — useful for opaque ids), and either form may be supplied
 * directly or returned from an async resolver. The async path runs
 * through ``useSuspenseQuery`` purely for the loading/error/data
 * ergonomics — the query key is scoped to this component instance
 * (``React.useId``), so nothing is shared across consumers. Caching is
 * the consumer's responsibility (e.g. wrap the function with
 * ``queryClient.ensureQueryData(...)``).
 *
 * Search matches each option's display label (which equals the wire
 * value for plain-string options). Consumers that need search-by-id
 * for labeled options should expose the id elsewhere in the row.
 *
 * Per the spec, dynamic enum lists are not paginated; consumers must
 * keep the option set small enough to fit in memory. Search filters
 * client-side only.
 */
export function EnumValueInput({
  comparator,
  value,
  onChange,
  options,
}: EnumValueInputProps) {
  const [searchValue, setSearchValue] = React.useState("");
  const instanceId = React.useId();

  if (isValueless(comparator)) {
    return null;
  }

  // TODO Use an MUI text input for search.
  // Or a tags autocomplete instead.
  return (
    <div className={styles.input}>
      <input
        type="search"
        className={styles.searchInput}
        placeholder="Search options..."
        aria-label="Search options"
        value={searchValue}
        onChange={(event) => setSearchValue(event.target.value)}
      />
      <ErrorBoundary fallback={renderErrorState}>
        <React.Suspense fallback={<LoadingState />}>
          <OptionList
            instanceId={instanceId}
            options={options}
            searchValue={searchValue}
            value={value}
            onChange={onChange}
          />
        </React.Suspense>
      </ErrorBoundary>
    </div>
  );
}

interface OptionListProps {
  instanceId: string;
  options: EnumOption[] | (() => Promise<EnumOption[]>);
  searchValue: string;
  value: string[];
  onChange: (next: string[]) => void;
}

function OptionList({
  instanceId,
  options,
  searchValue,
  value,
  onChange,
}: OptionListProps) {
  const isLazy = typeof options === "function";

  // The static path supplies `initialData`, so Suspense never activates
  // for it. The lazy path has no initial data and suspends until the
  // promise resolves. Either way, `data` is `EnumOption[]` in the body.
  const { data } = useSuspenseQuery({
    queryKey: [instanceId],
    queryFn: isLazy ? options : () => Promise.resolve(options),
    initialData: isLazy ? undefined : options,
  });

  // Normalize once per ``data`` change so the search and render passes
  // both operate on ``{ label, value }`` shape regardless of whether the
  // caller supplied plain strings or labeled objects. Search matches
  // ``label`` — what the user sees — not the opaque wire ``value``.
  const normalized = React.useMemo(() => data.map(normalizeOption), [data]);
  const filteredOptions = React.useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    if (needle === "") {
      return normalized;
    }
    return normalized.filter((option) =>
      option.label.toLowerCase().includes(needle),
    );
  }, [normalized, searchValue]);

  if (filteredOptions.length === 0) {
    return (
      <div className={styles.status} role="status" aria-live="polite">
        No matching options
      </div>
    );
  }

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  return (
    <ul
      className={styles.list}
      role="listbox"
      aria-label="Filterable values"
      aria-multiselectable="true"
    >
      {filteredOptions.map((option) => {
        const isSelected = value.includes(option.value);
        return (
          <li key={option.value} role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={isSelected}
              className={styles.row}
              onClick={() => toggleOption(option.value)}
            >
              <Checkbox
                size="small"
                checked={isSelected}
                tabIndex={-1}
                disableRipple
                className={styles.checkbox}
              />
              <span className={styles.rowLabel}>{option.label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function LoadingState() {
  return (
    <div className={styles.status} role="status" aria-live="polite">
      <CircularProgress size="1rem" />
      <span>Loading options…</span>
    </div>
  );
}

function renderErrorState() {
  return (
    <div className={classNames(styles.status, styles.error)} role="alert">
      Couldn’t load options. Reopen the menu to retry.
    </div>
  );
}
